import {useCallback, useEffect, useRef, useState} from 'react';
import {Virtual} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Swiper as SwiperClass} from 'swiper/types';
import {calcPercent} from '../../../common/shared/utils/calcPercent';
import {preventDefaultEvent} from '../../../common/shared/utils/preventDefaultEvent.function';
import {stopPropagationEvent} from '../../../common/shared/utils/stopPropagationEvent.function';
import {twMerge} from '../../../common/twMerge';
import {useViewerMenuHandler} from '../../../domain/content/[seriesId]/viewer/[productId]/hook/useViewerMenuHandler';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {getInternalAssetImage} from '../../atoms/StaticImage/getInternalAssetImage.function';
import {STATIC_IMAGE_INFO_MAP} from '../../atoms/StaticImage/StaticImageInfoMap.constant';
import {getViewerSlides} from './getViewerSlides.function';
import {ViewerSlide} from './ViewerSlide';
import {ViewerSlideSwiperProps} from './ViewerSlideSwiperProps.interface';

function ViewerSlideSwiperMain({
  isMobile,
  isTwoPageLayout,
  isBlackSpaceColor,
  isReverseDir,
  onSwiper,
  onSlideChange,
  children,
  className,
  ...props
}: ViewerSlideSwiperProps) {
  const touchStartPositisionX = useRef(0);
  const swiperRef = useRef<SwiperClass>();
  const [shouldAllowNextSwipe, setShouldAllowNextSwipe] = useState(true);
  const {shouldUseNonstopWatching, handleMoveNextProduct} =
    useViewerMenuHandler({
      isMobile,
    });
  const viewerSlides = getViewerSlides(children);

  const slideNext = useCallback(
    (swiper?: SwiperClass) => {
      if (!swiper) {
        return;
      }
      const swiperLastIndex =
        (swiper.virtual?.slides || swiper.slides).length - 1;

      // 두쪽보기이고 정주행 모드 일때
      if (
        isTwoPageLayout &&
        shouldUseNonstopWatching &&
        swiper.activeIndex >= swiperLastIndex - 1
      ) {
        handleMoveNextProduct();
      }
      // 두쪽보기가 아니고 정주행 모드일때
      else if (
        !isTwoPageLayout &&
        shouldUseNonstopWatching &&
        swiper.activeIndex >= swiperLastIndex
      ) {
        handleMoveNextProduct();
      }
      // 일반적인 경우
      else {
        swiper.slideNext();
      }
    },
    [handleMoveNextProduct, isTwoPageLayout, shouldUseNonstopWatching]
  );

  const onReleaseHandler = useCallback(
    (event: MouseEvent | TouchEvent | React.TouchEvent) => {
      const swiper = swiperRef.current;
      if (!swiper || !shouldUseNonstopWatching) {
        return;
      }
      const boundingRect = swiper.wrapperEl.getBoundingClientRect();
      const touchEndPositisionX =
        'touches' in event
          ? event.changedTouches[0].clientX - boundingRect.x
          : event.clientX - boundingRect.x;

      if (isReverseDir && touchStartPositisionX.current < touchEndPositisionX) {
        handleMoveNextProduct();
      } else if (
        !isReverseDir &&
        touchStartPositisionX.current > touchEndPositisionX
      ) {
        handleMoveNextProduct();
      }

      if (!isMobile) {
        window.removeEventListener('mouseup', onReleaseHandler);
      }
    },
    [handleMoveNextProduct, isMobile, isReverseDir, shouldUseNonstopWatching]
  );

  const onPressHandler = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const swiper = swiperRef.current;
      if (!swiper) {
        return;
      }

      const boundingRect = swiper.wrapperEl.getBoundingClientRect();
      touchStartPositisionX.current =
        'touches' in event
          ? event.touches[0].clientX - boundingRect.x
          : event.clientX - boundingRect.x;

      // mouseup 이벤트는 mousedown 이벤트가 일어난 곳 외에서 마우스를 떼면 발생하지 않아서 window에 걸어준다.
      if (!isMobile) {
        window.addEventListener('mouseup', onReleaseHandler, {
          once: true,
        });
      }
    },
    [isMobile, onReleaseHandler]
  );

  useEffect(() => {
    const wrapperDiv = swiperRef.current?.wrapperEl;
    if (shouldUseNonstopWatching && !shouldAllowNextSwipe && wrapperDiv) {
      if (isMobile) {
        wrapperDiv.addEventListener('touchstart', onPressHandler);
        wrapperDiv.addEventListener('touchend', onReleaseHandler);
      } else {
        wrapperDiv.addEventListener('mousedown', onPressHandler);
      }
    }

    return () => {
      const wrapperDiv = swiperRef.current?.wrapperEl;
      if (wrapperDiv) {
        if (isMobile) {
          wrapperDiv.removeEventListener('touchstart', onPressHandler);
          wrapperDiv.removeEventListener('touchend', onReleaseHandler);
        } else {
          wrapperDiv.removeEventListener('mousedown', onPressHandler);
        }
      }
    };
  }, [
    isMobile,
    onPressHandler,
    onReleaseHandler,
    shouldAllowNextSwipe,
    shouldUseNonstopWatching,
  ]);

  const swiper = swiperRef.current;

  // swiper의 인덱스 값을 초기화하고 trainsition이 일어날때마다 업데이트 해줍니다
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  useEffect(() => {
    if (!swiper) return;
    setCurrentIndex(swiper.activeIndex);
  }, [swiper]);
  const onSlideChangeTransitionEnd = useCallback((swiper: SwiperClass) => {
    setCurrentIndex(swiper.activeIndex);
  }, []);

  // 정주행 모드를 체크합니다
  useEffect(() => {
    if (!swiper) return;
    const swiperLastIndex =
      (swiper.virtual?.slides || swiper.slides).length - 1;

    // 두쪽보기 정주행 모드일때 마지막페이지에 있음을 체크해 스와이프를 막습니다.
    if (
      isTwoPageLayout &&
      shouldUseNonstopWatching &&
      currentIndex >= swiperLastIndex - 1
    ) {
      setShouldAllowNextSwipe(false);
      return;
    }

    // 한쪽보기 정주행 모드일때 마지막페이지에 있음을 체크해 스와이프를 막습니다.
    if (
      !isTwoPageLayout &&
      shouldUseNonstopWatching &&
      currentIndex >= swiperLastIndex
    ) {
      setShouldAllowNextSwipe(false);
      return;
    }

    // 정주행 모드가 아니고 마지막페이지도 아니라면 스와이프를 막아서는 안됩니다.
    setShouldAllowNextSwipe(true);
  }, [currentIndex, isTwoPageLayout, shouldUseNonstopWatching, swiper]);

  /* 좌우 키보드 이벤트 */
  useEffect(() => {
    if (!swiper) return;
    const onArrowDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (e.code === 'ArrowLeft') {
        isReverseDir ? slideNext(swiper) : swiper.slidePrev();
      } else if (e.code === 'ArrowRight') {
        isReverseDir ? swiper.slidePrev() : slideNext(swiper);
      }
    };
    window.addEventListener('keydown', onArrowDown);
    return () => {
      window.removeEventListener('keydown', onArrowDown);
    };
  }, [isReverseDir, slideNext, swiper]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: isTwoPageLayout 변경만 알고 싶기 때문에 디펜던시에 추가합니다.
  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper) return;

    // isTwoPageLayout 변경 시 슬라이드의 사이즈와 위치 업데이트가 안 되는 문제가 있어서 강제로 업데이트 합니다.
    swiper.virtual.update(true);
  }, [isTwoPageLayout]);

  const leftImageInfo = STATIC_IMAGE_INFO_MAP[IconType.ViewerArrowLeft];
  const rightImageInfo = STATIC_IMAGE_INFO_MAP[IconType.ViewerArrowRight];

  return (
    <div
      className="flex h-full w-full items-center justify-center"
      onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        // 모바일에서만 좌우 터치시 페이지 이동 기능을 제공합니다.
        if (!swiper || !isMobile) return;
        const clickedSide = calcClickedSideByRatio(
          swiper.clickedSlide.getBoundingClientRect(),
          event
        );
        if (clickedSide === RectSideType.Left) {
          isReverseDir ? slideNext(swiper) : swiper.slidePrev();
          // 터치 후 뷰어 메뉴가 안열리게 처리함
          event.stopPropagation();
        } else if (clickedSide === RectSideType.Right) {
          isReverseDir ? swiper.slidePrev() : slideNext(swiper);
          // 터치 후 뷰어 메뉴가 안열리게 처리함
          event.stopPropagation();
        }
      }}
    >
      {!isMobile && (
        <div className="z-150 w-112pxr shrink-0 select-none pr-20pxr">
          <div
            className={twMerge(
              'cursor-pointer p-30pxr',
              !isReverseDir &&
                swiper?.isBeginning &&
                'pointer-events-none opacity-30',
              isReverseDir &&
                swiper?.isEnd &&
                !shouldUseNonstopWatching &&
                'pointer-events-none opacity-30'
            )}
            onClick={e => {
              stopPropagationEvent(e);
              isReverseDir ? slideNext(swiper) : swiper?.slidePrev();
            }}
          >
            {isBlackSpaceColor ? (
              <img
                alt={leftImageInfo.alt}
                src={getInternalAssetImage(leftImageInfo.srcDark ?? '')}
                onSelect={preventDefaultEvent}
                onDragStart={preventDefaultEvent}
                onContextMenu={preventDefaultEvent}
              />
            ) : (
              <img
                alt={leftImageInfo.alt}
                src={getInternalAssetImage(leftImageInfo.srcLight ?? '')}
                onSelect={preventDefaultEvent}
                onDragStart={preventDefaultEvent}
                onContextMenu={preventDefaultEvent}
              />
            )}
          </div>
        </div>
      )}

      {/* TODO : 이미지 넘김 효과를 없애는 방법을 연구해야합니다. 우선 효과 있는 방향으로 오픈하기로 했지만, 이후 CS가 들어오면 효과가 제거되어야 할 수 있습니다 */}
      <Swiper
        className={twMerge('mx-0 h-full w-full select-none', className)}
        id="viewer-slide-swiper" // globals.css 에서 스타일 지정하기 위한 ID. swiper-wrapper 에 flex 적용 위함(옵션은 못찾음)
        modules={[Virtual]}
        virtual
        observer
        threshold={20}
        slidesPerView={isTwoPageLayout && !isMobile ? 2 : 1}
        slidesPerGroup={isTwoPageLayout && !isMobile ? 2 : 1}
        // 정주행이 아닌경우 swiper를 막으면 안됩니다. 정주행시에만 막고 wrapperDiv에 터치 이벤트를 붙힙니다.
        allowSlideNext={isReverseDir ? true : shouldAllowNextSwipe}
        allowSlidePrev={true}
        onSlideChange={onSlideChange}
        onSwiper={swiper => {
          swiperRef.current = swiper;
          onSwiper?.(swiper);
        }}
        dir={isReverseDir ? 'rtl' : ''}
        // mouse 이벤트를 활성화하시 위해 false로 세팅
        touchStartPreventDefault={false}
        onSlideChangeTransitionEnd={onSlideChangeTransitionEnd}
        {...props}
      >
        {viewerSlides.map((slide, index) => (
          <SwiperSlide key={index} dir={'ltr'}>
            {slide}
          </SwiperSlide>
        ))}
      </Swiper>
      {!isMobile && (
        <div className="z-150 w-112pxr shrink-0 select-none pl-20pxr">
          <div
            className={twMerge(
              'cursor-pointer p-30pxr',
              !isReverseDir &&
                swiper?.isEnd &&
                !shouldUseNonstopWatching &&
                'pointer-events-none opacity-30',
              isReverseDir &&
                swiper?.isBeginning &&
                'pointer-events-none opacity-30'
            )}
            onClick={e => {
              stopPropagationEvent(e);
              isReverseDir ? swiper?.slidePrev() : slideNext(swiper);
            }}
          >
            {isBlackSpaceColor ? (
              <img
                alt={rightImageInfo.alt}
                src={getInternalAssetImage(rightImageInfo.srcDark ?? '')}
                onSelect={preventDefaultEvent}
                onDragStart={preventDefaultEvent}
                onContextMenu={preventDefaultEvent}
              />
            ) : (
              <img
                alt={rightImageInfo.alt}
                src={getInternalAssetImage(rightImageInfo.srcLight ?? '')}
                onSelect={preventDefaultEvent}
                onDragStart={preventDefaultEvent}
                onContextMenu={preventDefaultEvent}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function calcClickedSideByRatio(
  rect: DOMRect,
  event: React.MouseEvent<HTMLDivElement, MouseEvent>
): RectSideType {
  const positionX = event.clientX - rect.x;
  const percent = calcPercent({
    current: Math.abs(positionX),
    max: rect.width,
  });
  if (percent < 25) {
    return RectSideType.Left;
  } else if (percent > 75) {
    return RectSideType.Right;
  } else {
    return RectSideType.None;
  }
}

enum RectSideType {
  Left = 0,
  Right = 1,
  None = 2,
}

export const ViewerSlideSwiper = Object.assign(ViewerSlideSwiperMain, {
  Slide: ViewerSlide,
});
